import React, { useState, useEffect, useRef } from 'react'
import { FormFieldEntry } from '../components'
import { RequiredValidator, GzServerAddressValidator } from '../utils/validators'
import { checkApiDetails, toastNotification } from '../utils/apiFunctions'
import { useEventListener } from '../utils/customHooks'

let gzApiIntegrationDetails = {
    gzApiKey: '',
    gzServerAddress: '',
    phone: '',
    address: '',
    managed: false
}

let temporaryGzApiKey = null;
let temporaryGzServerAddress = null;

const CompanyDetails = ({ companySettings, updateTenantData }) => {
    const [areApiSettingsChanged, setAreApiSettingsChanged] = useState(false);
    const [gzApiKey, setGzApiKey] = useState(companySettings.gzSettings.gzApiKey);
    const isGzApiKeyValid = companySettings.gzSettings.gravityZoneIntegrationStatus?.isGzKeyValid
    const errorMessage = companySettings.gzSettings.gravityZoneIntegrationStatus?.message
    
    const [gzServerAddress, setGzServerAddress] = useState(companySettings.gzSettings.gzServerAddress);

    let gzApiKeyInputRef = useRef(null);
    let gzApiServerInputRef = useRef(null);
    let validateApiBtnRef = useRef(null);
    let undoBtnRef = useRef(null);

    useEffect(() => {
        gzApiKeyInputRef.current.validators = [RequiredValidator('Required field')];
        gzApiServerInputRef.current.validators = [RequiredValidator('Required field'), GzServerAddressValidator('Invalid server address!')];

        if (typeof isGzApiKeyValid !== 'undefined' && typeof errorMessage !== 'undefined' && !isGzApiKeyValid) {
            toastNotification(errorMessage, 'error', false)
        }
    }, []);

    useEffect(() => {
        if (areApiSettingsChanged)
        {
            undoBtnRef.current.addEventListener('click', handleUndo);
            validateApiBtnRef.current.disabled = false;
            return;
        }

        validateApiBtnRef.current.disabled = true;
    }, [areApiSettingsChanged])

    const handleChange = (e) => {
        temporaryGzApiKey = gzApiKey;
        temporaryGzServerAddress = gzServerAddress;

        if (e.detail.sourceEvent !== gzApiKey || e.detail.sourceEvent !== gzServerAddress) {
            e.srcElement.id === 'gzApiUrlField_input' ? setGzServerAddress(e.detail.sourceEvent) : setGzApiKey(e.detail.sourceEvent);
            setAreApiSettingsChanged(true);
        }
    }

    const handleUndo = () => {
        gzApiKeyInputRef.current.value = temporaryGzApiKey;
        gzApiServerInputRef.current.value = temporaryGzServerAddress;

        setAreApiSettingsChanged(false);
    }

    const handleApiValidation = async () => {
        validateApiBtnRef.current.showSpinner = true;

        let newGzApiDetails = {
            gzApiKey: gzApiKeyInputRef.current.value.trim(),
            gzServerAddress: gzApiServerInputRef.current.value
        }

        if (!gzApiKeyInputRef.current.valid || !gzApiServerInputRef.current.valid) {
            validateApiBtnRef.current.showSpinner = false;
            return;
        }

        if (gzApiKeyInputRef.current.value !== gzApiKey) {
            newGzApiDetails.gzApiKey = gzApiKeyInputRef.current.value$.destination._buffer[0];
        }

        if (gzApiServerInputRef.current.value !== gzServerAddress) {
            newGzApiDetails.gzServerAddress = gzApiServerInputRef.current.value$.destination._buffer[0];
        }

        let apiDetailsValidationResult = await checkApiDetails(newGzApiDetails);

        if (apiDetailsValidationResult.isValid) {

            gzApiIntegrationDetails = { ...newGzApiDetails };
            gzApiIntegrationDetails.companyName = apiDetailsValidationResult.companyName;
            gzApiIntegrationDetails.companyType = apiDetailsValidationResult.companyType;
            gzApiIntegrationDetails.phone = apiDetailsValidationResult.phone;
            gzApiIntegrationDetails.address = apiDetailsValidationResult.address;
            gzApiIntegrationDetails.managed = apiDetailsValidationResult.managed;

            updateTenantData(gzApiIntegrationDetails);

            toastNotification('GravityZone API key validated.', 'success');
            validateApiBtnRef.current.showSpinner = false;

            setAreApiSettingsChanged(false);
            return;
        }

        toastNotification(apiDetailsValidationResult.message, 'error', false);
        validateApiBtnRef.current.showSpinner = false;
    }

    useEventListener({ eventName: 'click', handler: handleApiValidation, ref: validateApiBtnRef });
    useEventListener({ eventName: 'change', handler: handleChange, ref: gzApiKeyInputRef });
    useEventListener({ eventName: 'change', handler: handleChange, ref: gzApiServerInputRef });

    return (
        <div className='pt-2 pl-8'>
            <div id='changeApiDetails-container'>
                <div className='mt-4'>
                    <gz-title fontSize="18" fontWeight="500">GravityZone credentials</gz-title>
                </div>
                <div className='mt-2'>
                    <gz-text fontSize="14">
                        Provide the GravityZone details needed for the integration.
                    </gz-text>
                </div>
                <div className='mt-6' id='apiDetailInputs-container'>
                    <FormFieldEntry
                        ref={gzApiKeyInputRef}
                        label="GravityZone API key"
                        formFieldId="gzApiKeyField"
                        typeOfEnclosing="gz-input"
                        width="320"
                        value={gzApiKey !== null ? gzApiKey : ''}
                        type='password'
                        placeholder="Insert API Key"
                    />
                    <FormFieldEntry
                        ref={gzApiServerInputRef}
                        label="GravityZone API access URL"
                        formFieldId="gzApiUrlField"
                        typeOfEnclosing="gz-input"
                        width="320"
                        value={gzServerAddress !== null ? gzServerAddress : ''}
                        placeholder="Insert API access URL"
                    />
                </div>
                <div className='mt-8 flex flex-row' id='validateButton-container'>
                    <gz-button id="validateApiDetailsBtn" ref={validateApiBtnRef} type="outline" color="primary">Validate</gz-button>
                    {areApiSettingsChanged && <div className='ml-96'>
                        <gz-button id="undoBtn" ref={undoBtnRef} type='outline' color='secondary'>Undo</gz-button>
                    </div>}
                </div>
            </div>
            <div id='myCompanyDetails-container'>
                <div className='mt-14'>
                    <gz-title fontSize="18" fontWeight="500">Company info</gz-title>
                </div>
                <div className='mt-1'>
                    <gz-text fontSize="14">
                        Details automatically filled in based on the GravityZone API integration.
                    </gz-text>
                </div>
                <div className='flex flex-col items-start mt-6'>
                    <FormFieldEntry
                        label="Company name"
                        formFieldId="gzIntegrationCompany"
                        typeOfEnclosing="gz-input"
                        width="240"
                        readonly
                        value={companySettings.gzCompanyDetails.name !== null ? companySettings.gzCompanyDetails.name : ''}
                        placeholder="My company"
                    />
                    <FormFieldEntry
                        label="Company type"
                        formFieldId="gzIntegrationCompanyType"
                        typeOfEnclosing="gz-input"
                        width="240"
                        readonly
                        placeholder="Company type"
                        value={companySettings.gzCompanyDetails.name ? (companySettings.gzCompanyDetails.type === 0 ? 'Partner' : 'Customer') : ''}
                    />
                    <FormFieldEntry
                        label="Address"
                        formFieldId="address"
                        typeOfEnclosing="gz-input"
                        width="240"
                        readonly
                        placeholder="Address"
                        value={companySettings.gzCompanyDetails?.address !== null ? companySettings.gzCompanyDetails.address : ''}
                    />
                    <FormFieldEntry
                        label="Phone"
                        formFieldId="phone"
                        typeOfEnclosing="gz-input"
                        width="240"
                        readonly
                        placeholder="Phone"
                        value={companySettings.gzCompanyDetails?.phone !== null ? companySettings.gzCompanyDetails.phone : ''}
                    />
                </div>
                <div className='mt-1'>
                <gz-checkbox gz-form-input="" disabled="true" color="primary" {... companySettings.gzCompanyDetails.managed ? {checked: true} : {}} label="Security managed by partner"></gz-checkbox>
                </div>
            </div>

        </div >
    )
}

export default CompanyDetails